import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';
import type { EventSummaryContext, ResolvedEntity } from './eventSummary';

/**
 * A row's entity references, as much as the Details column needs to name them.
 *
 * Most admin-side emitters write an `entity_id` and no metadata at all — a voided invoice,
 * a payment reminder and a comp all log the id and nothing else — so the summary has no
 * name to show until the referenced row is fetched. The attendance emitters are the mirror
 * image: they write `student_id`/`program_id` into the metadata rather than the columns.
 */
export interface EntityRefEvent {
  entity_type?: string | null;
  entity_id?: string | null;
  page_url?: string | null;
  metadata: Record<string, unknown>;
}

export interface EntityLookup {
  /** Keyed by `entity_id`. */
  entities: Record<string, ResolvedEntity>;
  /** Keyed by a student id found in metadata. */
  students: Record<string, string>;
  /** Keyed by a program id found in metadata. */
  programs: Record<string, string>;
  /** `line_item_configs.id` → name, for enrollment opt-out diffs. */
  lineItems: Record<string, string>;
}

export const EMPTY_LOOKUP: EntityLookup = { entities: {}, students: {}, programs: {}, lineItems: {} };

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const asUuid = (value: unknown): string | null => {
  const s = typeof value === 'string' ? value : '';
  return UUID_PATTERN.test(s) ? s : null;
};

export interface EntityRefs {
  invoiceIds: string[];
  paymentIds: string[];
  enrollmentIds: string[];
  programIds: string[];
  studentIds: string[];
  metaStudentIds: string[];
  metaProgramIds: string[];
  lineItemIds: string[];
}

const sorted = (set: Set<string>) => [...set].sort();

/** Groups the ids one page of events refers to by the table each must be read from. */
export function collectEntityRefs(events: EntityRefEvent[]): EntityRefs {
  const invoices = new Set<string>();
  const payments = new Set<string>();
  const enrollments = new Set<string>();
  const programs = new Set<string>();
  const students = new Set<string>();
  const metaStudents = new Set<string>();
  const metaPrograms = new Set<string>();
  const lineItems = new Set<string>();

  for (const event of events) {
    const id = asUuid(event.entity_id);
    if (id) {
      switch (event.entity_type) {
        case 'invoice': invoices.add(id); break;
        case 'payment': payments.add(id); break;
        case 'enrollment': enrollments.add(id); break;
        case 'program': programs.add(id); break;
        case 'student': students.add(id); break;
        default: break;
      }
    }

    const metadata = event.metadata ?? {};
    const metaStudent = asUuid(metadata.student_id);
    if (metaStudent) metaStudents.add(metaStudent);
    const metaProgram = asUuid(metadata.program_id);
    if (metaProgram) metaPrograms.add(metaProgram);

    // The diff holds `line_item_configs` ids, which mean nothing to a reader until they are
    // names. Events written before 3.69.0 carry `opt_out_items`; app_events is immutable
    // history, so both keys are resolved or those older entries render blank.
    const changes = metadata.changes as
      { metadata?: { opt_in_items?: unknown; opt_out_items?: unknown } } | undefined;
    const choiceIds = [
      ...(Array.isArray(changes?.metadata?.opt_in_items) ? changes!.metadata!.opt_in_items as unknown[] : []),
      ...(Array.isArray(changes?.metadata?.opt_out_items) ? changes!.metadata!.opt_out_items as unknown[] : []),
    ];
    if (choiceIds.length > 0) {
      for (const item of choiceIds) {
        const itemId = asUuid(item);
        if (itemId) lineItems.add(itemId);
      }
    }
  }

  return {
    invoiceIds: sorted(invoices),
    paymentIds: sorted(payments),
    enrollmentIds: sorted(enrollments),
    programIds: sorted(programs),
    studentIds: sorted(students),
    metaStudentIds: sorted(metaStudents),
    metaProgramIds: sorted(metaPrograms),
    lineItemIds: sorted(lineItems),
  };
}

/** PostgREST shapes for the lookup queries — the generated types spell embeds as arrays. */
interface StudentRow { id: string; first_name: string | null; last_name: string | null }
interface ProgramRow { id: string; name: string | null }
interface EnrollmentJoin { students: StudentRow | null; programs: ProgramRow | null }
interface EnrollmentRow extends EnrollmentJoin { id: string }
interface InvoiceRow {
  id: string;
  total_amount: number | null;
  amount_paid: number | null;
  status: string | null;
  due_date: string | null;
  enrollments: EnrollmentJoin | null;
}
interface PaymentRow {
  id: string;
  payment_method: string | null;
  invoices: { enrollments: EnrollmentJoin | null } | null;
}

const fullName = (student: StudentRow | null | undefined): string | null =>
  student ? [student.first_name, student.last_name].filter(Boolean).join(' ') || null : null;

/**
 * Resolves the entities one page of events points at, in one round of batched queries.
 *
 * Doing this at read time rather than only fixing the emitters is deliberate: it names the
 * rows already written, which a metadata backfill cannot reach.
 */
export function useEntityLookup(events: EntityRefEvent[] | undefined): EntityLookup {
  const refs = useMemo(() => collectEntityRefs(events ?? []), [events]);
  const hasWork = Object.values(refs).some(ids => ids.length > 0);

  const { data } = useQuery({
    queryKey: ['eventEntityLookup', refs],
    queryFn: async (): Promise<EntityLookup> => {
      const studentIds = [...new Set([...refs.studentIds, ...refs.metaStudentIds])];
      const programIds = [...new Set([...refs.programIds, ...refs.metaProgramIds])];

      const [invoiceRes, paymentRes, enrollmentRes, programRes, studentRes, lineItemRes] = await Promise.all([
        refs.invoiceIds.length
          // A plain embed, not `!inner`: an invoice with no enrollment should still resolve
          // its amount and status rather than vanish from the lookup.
          ? supabase.from('invoices')
              .select('id, total_amount, amount_paid, status, due_date, enrollments(students(first_name, last_name), programs(name))')
              .in('id', refs.invoiceIds)
          : null,
        refs.paymentIds.length
          // A refund logs only the payment id, so the child it belongs to is two hops away.
          ? supabase.from('payments')
              .select('id, payment_method, invoices(enrollments(students(first_name, last_name), programs(name)))')
              .in('id', refs.paymentIds)
          : null,
        refs.enrollmentIds.length
          ? supabase.from('enrollments')
              .select('id, students(first_name, last_name), programs(name)')
              .in('id', refs.enrollmentIds)
          : null,
        programIds.length
          ? supabase.from('programs').select('id, name').in('id', programIds)
          : null,
        studentIds.length
          ? supabase.from('students').select('id, first_name, last_name').in('id', studentIds)
          : null,
        refs.lineItemIds.length
          ? supabase.from('line_item_configs').select('id, name').in('id', refs.lineItemIds)
          : null,
      ]);

      const lookup: EntityLookup = { entities: {}, students: {}, programs: {}, lineItems: {} };

      for (const invoice of (invoiceRes?.data ?? []) as unknown as InvoiceRow[]) {
        const total = invoice.total_amount != null ? Number(invoice.total_amount) : null;
        const paid = invoice.amount_paid != null ? Number(invoice.amount_paid) : 0;
        lookup.entities[invoice.id] = {
          kind: 'invoice',
          studentName: fullName(invoice.enrollments?.students),
          programName: invoice.enrollments?.programs?.name ?? null,
          totalAmount: total,
          balanceDue: total != null ? total - paid : null,
          status: invoice.status,
          dueDate: invoice.due_date,
        };
      }

      for (const payment of (paymentRes?.data ?? []) as unknown as PaymentRow[]) {
        const joined = payment.invoices?.enrollments ?? null;
        lookup.entities[payment.id] = {
          kind: 'payment',
          studentName: fullName(joined?.students),
          programName: joined?.programs?.name ?? null,
          method: payment.payment_method,
        };
      }

      for (const enrollment of (enrollmentRes?.data ?? []) as unknown as EnrollmentRow[]) {
        lookup.entities[enrollment.id] = {
          kind: 'enrollment',
          studentName: fullName(enrollment.students),
          programName: enrollment.programs?.name ?? null,
        };
      }

      for (const program of (programRes?.data ?? []) as unknown as ProgramRow[]) {
        lookup.programs[program.id] = program.name ?? '';
        if (refs.programIds.includes(program.id)) {
          lookup.entities[program.id] = { kind: 'program', name: program.name };
        }
      }

      for (const student of (studentRes?.data ?? []) as unknown as StudentRow[]) {
        const name = fullName(student);
        lookup.students[student.id] = name ?? '';
        if (refs.studentIds.includes(student.id)) {
          lookup.entities[student.id] = { kind: 'student', name };
        }
      }

      for (const config of (lineItemRes?.data ?? []) as unknown as ProgramRow[]) {
        lookup.lineItems[config.id] = config.name ?? '';
      }

      return lookup;
    },
    enabled: hasWork,
    // The referenced rows are names and amounts, not the event stream; they barely move.
    staleTime: 5 * 60 * 1000,
  });

  return data ?? EMPTY_LOOKUP;
}

/** Builds the third argument to `formatEventSummary` for one row. */
export function summaryContext(event: EntityRefEvent, lookup: EntityLookup): EventSummaryContext {
  const metadata = event.metadata ?? {};
  const studentId = asUuid(metadata.student_id);
  const programId = asUuid(metadata.program_id);
  return {
    page_url: event.page_url ?? null,
    entity: event.entity_id ? lookup.entities[event.entity_id] ?? null : null,
    student: studentId ? lookup.students[studentId] || null : null,
    program: programId ? lookup.programs[programId] || null : null,
    lineItems: lookup.lineItems,
  };
}
